import { useField } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';

import { InputField } from '../../../components/input/InputField';
import { SimpleSelectField } from '../../../components/select/SimpleSelectField';
import { COUNTRY_VALUES } from '../../../utils/address';
import { findPostalCodes, type LocationPostalCode } from '../models';
import { useTranslation } from '../../../contexts/translation-context';
import { InputWrapper } from 'components/InputWrapper';
import classNames from '@utils/classnames';
import { controlClassName, popoverItemClassName, popoverPanelClassName } from 'components/form/control';

type PostalCodeSuggestion = LocationPostalCode;

export interface IAutocompletePostalcodeValues {
  postalCodeName?: string;
  cityName?: string;
  countryName?: string;
  onAutocomplete?: (postalCode: string, city: string, longitude: number, latitude: number) => void;
}

export const AutocompletePostalcode: React.FC<IAutocompletePostalcodeValues> = (props) => {
  const { postalCodeName = 'postalCode', cityName = 'city', countryName = 'country', onAutocomplete } = props;
  const [isFocused, setIsFocused] = useState(false);
  const [suggestions, setSuggestions] = useState<PostalCodeSuggestion[]>([]);
  const requestKeyRef = useRef(0);
  const onAutocompleteRef = useRef(onAutocomplete);
  onAutocompleteRef.current = onAutocomplete;
  const { i18n } = useTranslation();

  const [postalCodeField, postalCodeMeta, postalCodeHelpers] = useField({ name: postalCodeName });
  const [countryField] = useField({ name: countryName });
  const [, , cityHelpers] = useField({ name: cityName });

  const postalCode = postalCodeField.value as string;
  const country = countryField.value as { key?: string } | null;

  const applySuggestion = (suggestion: PostalCodeSuggestion) => {
    postalCodeHelpers.setValue(suggestion.postalCode);
    cityHelpers.setValue(suggestion.placeName);
    onAutocompleteRef.current?.(suggestion.postalCode, suggestion.placeName, suggestion.longitude, suggestion.latitude);
    setIsFocused(false);
  };

  useEffect(() => {
    const query = postalCode;
    const countryCode = country?.key;
    if (!query || !countryCode) {
      setSuggestions([]);
      return;
    }

    const requestKey = ++requestKeyRef.current;
    findPostalCodes(query, countryCode)
      .then((result) => {
        if (requestKeyRef.current !== requestKey) {
          return;
        }

        setSuggestions(result ?? []);
      })
      .catch((err) => {
        console.error(err);
      });
  }, [postalCode, country?.key]);

  const isOpen = isFocused && suggestions.length > 0;
  const postalCodeError = postalCodeMeta.error;

  return (
    <div className="flex gap-4">
      <div className="flex-1">
        <SimpleSelectField
          labelText={i18n('customerPortal.locations.countryCode')}
          items={COUNTRY_VALUES}
          name={countryName}
        />
      </div>
      <div className="relative flex-1">
        <InputWrapper labelText={i18n('customerPortal.locations.postalCode')} invalidText={postalCodeError}>
          {/* Fully controlled: Radix must not dismiss when the Anchor input (outside Content) keeps focus. */}
          <Popover.Root open={isOpen}>
            <Popover.Anchor asChild>
              <input
                name={postalCodeName}
                className={classNames(
                  controlClassName({ isInvalid: !!postalCodeError, focusWithin: false }),
                  'flex px-2.5',
                )}
                value={postalCodeField.value}
                onChange={postalCodeField.onChange}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    setIsFocused(false);
                  }
                }}
                autoComplete="off"
              />
            </Popover.Anchor>

            <Popover.Portal>
              {/* Portaled Content must carry z-dropdown or it paints under dialogs. */}
              <Popover.Content
                align="start"
                sideOffset={4}
                onOpenAutoFocus={(event) => event.preventDefault()}
                onCloseAutoFocus={(event) => event.preventDefault()}
                className={classNames(popoverPanelClassName, 'max-h-60 pointer-events-auto')}
                style={{
                  minWidth: 'var(--radix-popover-trigger-width)',
                  maxHeight: 'var(--radix-popover-content-available-height)',
                }}
              >
                {suggestions.map((suggestion) => {
                  const label = `${suggestion.postalCode} - ${suggestion.placeName}`;
                  return (
                    <div
                      key={label}
                      className={popoverItemClassName}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        applySuggestion(suggestion);
                      }}
                    >
                      {label}
                    </div>
                  );
                })}
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        </InputWrapper>
      </div>
      <div className="flex-2">
        <InputField labelText={i18n('customerPortal.locations.city')} type="text" name={cityName} />
      </div>
    </div>
  );
};
